import React, { useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  CallEnd,
  HighQuality,
  Mic,
  MicOff,
  PrivacyTipOutlined,
  SwitchCamera,
  Videocam,
  VideocamOff,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import useAgora from "./useAgora";
import useAlert from "./useAlert";

import { CameraSwitchModal } from "./cameraUtils";

import "./videoCall.css";
import ConnectionQualityIndicator from "components/consultation/call/ConnectionQualityIndicator";
import ConsultationElapsedTimer from "components/consultation/call/ConsultationElapsedTimer";

/*
 * Patient-facing quality choices.
 * "stable" exists only as an internal auto-ladder step and is never shown.
 */
const QUALITY_CHOICES = [
  {
    value: "auto",
    label: "Auto",
    hint: "Adapts to your network (Recommended)",
  },
  {
    value: "detail",
    label: "High Quality",
    hint: "Sharper image for the doctor (uses more data)",
  },
  {
    value: "data-save",
    label: "Data-save",
    hint: "Lowest mobile data usage",
  },
];

const VideoCall = ({
  appId,
  channelName,
  token,
  onCallEnd,
  appointmentData,

  providerName,
  appointmentTimeLabel,
  slotAt,

  serverNow,
  hardEndsAt,

  /*
   * true  -> microphone + camera
   * false -> microphone only
   *
   * Privacy Mode passes false.
   */
  startWithVideo = true,

  /*
   * Optional: async () => string
   * Returns a fresh RTC token from the token server.
   * Used to renew access before the current token expires.
   */
  fetchFreshToken,
}) => {
  const { showAlert, AlertComponent } = useAlert({
    mode: "overlay",
  });

  const [openCameraModal, setOpenCameraModal] = useState(false);
  const [isLocalStreamVisible, setIsLocalStreamVisible] = useState(true);
  const [isQualityDialogOpen, setIsQualityDialogOpen] = useState(false);

  const joinChannelRef = useRef(null);

  /*
   * uid -> audio track that has already been played.
   * Prevents audioTrack.play() from re-running on every remoteUsers
   * change, while still playing a NEW track object if a remote user
   * unpublishes and republishes their microphone.
   */
  const playedAudioRef = useRef(new Map());

  const {
    joinChannel,
    leaveChannel,
    disconnectChannel,
    toggleMute,
    toggleVideo,
    changeOptimizationMode,
    revertToAuto,
    modeSource,

    isJoined,
    isJoining,
    isMuted,
    isVideoStopped,
    optimizationMode,
    remoteUsers,
    localTracks,
    localVideoVersion,

    cameraUnavailable,
    permissionHelper,
    setPermissionsHelper,
    connectionError,
    networkQuality,

    isReconnecting,
    phoneFallbackSuggested,
    videoSuspendedByNetwork,
  } = useAgora({
    appId,
    channelName,
    token,
    onCallEnd,
    showAlert,
    appointmentData,
    serverNow,
    hardEndsAt,
    startWithVideo,
    fetchFreshToken,
  });

  useEffect(() => {
    joinChannelRef.current = joinChannel;
  }, [joinChannel]);

  /*
   * The zero-delay timer prevents React StrictMode's
   * development-only effect replay from creating two
   * overlapping join attempts.
   */
  useEffect(() => {
    const timerId = window.setTimeout(() => {
      void joinChannelRef.current?.();
    }, 0);

    return () => {
      window.clearTimeout(timerId);

      void disconnectChannel({
        notifyCallEnd: false,
        reason: "video-unmounted",
      });
    };
  }, [appId, channelName, token, disconnectChannel]);

  /*
   * Self-view. localVideoVersion changes when the camera is recovered
   * AFTER the initial join, so the new track gets attached.
   */
  useEffect(() => {
    if (!isJoined || !localTracks.current.videoTrack) {
      return;
    }

    try {
      localTracks.current.videoTrack.play("local-stream");
    } catch (error) {
      console.error("Unable to render local video", error);
    }
  }, [isJoined, localTracks, localVideoVersion]);

  /*
   * Remote containers exist after remoteUsers renders,
   * so tracks are played here instead of directly inside
   * Agora's user-published event.
   *
   * Video: play() is safe to repeat (container may have re-rendered).
   * Audio: played once per track object.
   */
  useEffect(() => {
    const activeUids = new Set();

    remoteUsers.forEach((user) => {
      activeUids.add(user.uid);

      if (user.videoTrack) {
        try {
          user.videoTrack.play(`remote-stream-${user.uid}`);
        } catch (error) {
          console.error("Unable to render remote video", error);
        }
      }

      if (
        user.audioTrack &&
        playedAudioRef.current.get(user.uid) !== user.audioTrack
      ) {
        try {
          user.audioTrack.play();
          playedAudioRef.current.set(user.uid, user.audioTrack);
        } catch (error) {
          console.error("Unable to play remote audio", error);
        }
      }
    });

    Array.from(playedAudioRef.current.keys()).forEach((uid) => {
      if (!activeUids.has(uid)) {
        playedAudioRef.current.delete(uid);
      }
    });
  }, [remoteUsers]);

  const handleRetryJoin = () => {
    void joinChannel();
  };

  const handleModeSelect = async (mode) => {
    const ok = await changeOptimizationMode(mode);
    if (ok) setIsQualityDialogOpen(false);
  };

  const handleRevertToAuto = async () => {
    await revertToAuto();
    setIsQualityDialogOpen(false);
  };

  const handleSwitchCameraComplete = () => {
    setOpenCameraModal(false);
    showAlert("success", "alert", "Camera switched.", "");
  };

  const hasLocalVideo = Boolean(localTracks.current.videoTrack);

  /*
   * The auto ladder may step down to the internal "stable" preset.
   * For the patient that is still "Auto", so highlight Auto.
   */
  const displayedMode =
    optimizationMode === "stable" ? "auto" : optimizationMode;

  const cameraLooksOff = isVideoStopped || videoSuspendedByNetwork;

  const permissionTitle = cameraUnavailable
    ? "Camera access is blocked"
    : startWithVideo
      ? "Allow camera and microphone"
      : "Allow microphone";

  const permissionMessage = cameraUnavailable
    ? "Your microphone is working, but the camera could not start. You can continue with audio or return to the waiting room to check your camera."
    : startWithVideo
      ? "Allow camera and microphone access, then retry the connection."
      : "Allow microphone access, then retry the connection.";

  const connectionStatusClass = isJoining
    ? "video-status video-status-connecting"
    : isJoined
      ? "video-status video-status-connected"
      : "video-status video-status-disconnected";

  const localPlaceholderText = isJoining ? "Starting camera…" : "Audio only";

  return (
    <Box className="video-call-container">
      <Dialog
        open={isQualityDialogOpen}
        onClose={() => setIsQualityDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>My camera quality</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This controls the video you send to the doctor. Data-save also
            reduces the video you receive, to use less mobile data.
          </Typography>

          <Stack spacing={1}>
            {QUALITY_CHOICES.map((choice) => (
              <Button
                key={choice.value}
                fullWidth
                variant={
                  displayedMode === choice.value ? "contained" : "outlined"
                }
                onClick={() => handleModeSelect(choice.value)}
                sx={{ justifyContent: "flex-start", textAlign: "left" }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {choice.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {choice.hint}
                  </Typography>
                </Box>
              </Button>
            ))}
          </Stack>

          {modeSource === "manual" && (
            <Button size="small" sx={{ mt: 2 }} onClick={handleRevertToAuto}>
              Switch back to Auto
            </Button>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsQualityDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        className="video-call-header"
      >
        <Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mt: 0.5 }}
          >
            <Stack direction="row" spacing={2}>
              {!startWithVideo && <PrivacyTipOutlined />}
              <h3>{startWithVideo ? "Video Consultation" : "Consultation"}</h3>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Scheduled
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {appointmentTimeLabel || "Unavailable"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Elapsed
                </Typography>
                <ConsultationElapsedTimer
                  slotAt={slotAt}
                  serverNow={serverNow}
                  hardEndsAt={hardEndsAt}
                />
              </Box>
            </Stack>
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          paddingBottom={1}
        >
          {isJoined && (
            <ConnectionQualityIndicator status={networkQuality?.status} />
          )}

          <Box className={connectionStatusClass}>
            {isJoining
              ? "Connecting"
              : isJoined
                ? isReconnecting
                  ? "Reconnecting"
                  : "Connected"
                : "Disconnected"}
          </Box>
        </Stack>
      </Stack>

      {permissionHelper && (
        <Paper elevation={0} className="permission-panel">
          <Typography variant="h6" component="h3">
            {permissionTitle}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {permissionMessage}
          </Typography>

          <Button
            variant="outlined"
            size="small"
            onClick={() => setPermissionsHelper(false)}
          >
            Close
          </Button>
        </Paper>
      )}

      <Box className="video-stage">
        <Box className="video-alert-overlay">
          {AlertComponent}

          {connectionError && (
            <Alert
              severity="error"
              action={
                !isJoining && !isJoined ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleRetryJoin}
                  >
                    Retry
                  </Button>
                ) : null
              }
            >
              {connectionError}
            </Alert>
          )}

          {isJoined && isReconnecting && (
            <Alert severity="warning">
              Reconnecting… your connection dropped. Keep this page open.
            </Alert>
          )}

          {isJoined && phoneFallbackSuggested && (
            <Alert severity="error">
              We still can't reconnect. You can continue by phone or reschedule.
            </Alert>
          )}

          {isJoined && networkQuality?.poorPersistent && (
            <Alert severity="warning">
              Your network connection is unstable.
            </Alert>
          )}

          {isJoined && videoSuspendedByNetwork && (
            <Alert severity="info">
              Audio-only mode: weak connection. Video resumes when it improves,
              or tap the camera button to turn it back on.
            </Alert>
          )}

          {startWithVideo && cameraUnavailable && (
            <Alert severity="warning">
              {isJoined
                ? "We couldn't start your camera yet. Your consultation continues with audio, and we'll keep trying to connect your camera."
                : "We couldn't start your camera. Your consultation can continue with audio."}
            </Alert>
          )}
        </Box>

        <Box
          className={
            remoteUsers.length > 1
              ? "remote-video-grid remote-video-grid-multiple"
              : "remote-video-grid"
          }
        >
          {remoteUsers.length ? (
            remoteUsers.map((user) => (
              <Box key={user.uid} className="remote-video-tile">
                <div
                  id={`remote-stream-${user.uid}`}
                  className="remote-video-stream"
                />

                {!user.videoTrack && (
                  <Box className="remote-audio-placeholder">
                    <Typography>
                      {user.hasVideo
                        ? "Connecting video…"
                        : "Audio participant"}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Box className="waiting-participant">
              <Typography variant="h6">
                Waiting for the health professional
              </Typography>

              <Typography variant="body2" color="inherit">
                Keep this page open. The other participant will appear after
                joining.
              </Typography>
            </Box>
          )}
        </Box>

        {/*
         * Self-view is always mounted; hiding is CSS-only so the Agora
         * <video> element stays attached and does not go black on toggle.
         */}
        <Box
          className="local-video-wrapper"
          sx={{ display: isLocalStreamVisible ? "block" : "none" }}
        >
          <div id="local-stream" className="local-video-stream" />

          {!hasLocalVideo && (
            <Box className="local-audio-placeholder">
              <Typography variant="caption">{localPlaceholderText}</Typography>
            </Box>
          )}

          <span className="local-video-label">You</span>
        </Box>

        {isJoining && (
          <Box className="video-connecting-overlay">
            <Typography>Connecting to consultation...</Typography>
          </Box>
        )}
      </Box>

      <Box className="controls-container">
        <Tooltip title={isMuted ? "Unmute microphone" : "Mute microphone"}>
          <span>
            <IconButton
              aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
              onClick={toggleMute}
              disabled={!isJoined}
              className={
                isMuted
                  ? "video-control-button control-disabled-state"
                  : "video-control-button"
              }
            >
              {isMuted ? <MicOff /> : <Mic />}
            </IconButton>
          </span>
        </Tooltip>

        {startWithVideo && (
          <Tooltip title={cameraLooksOff ? "Start camera" : "Stop camera"}>
            <span>
              <IconButton
                aria-label={cameraLooksOff ? "Start camera" : "Stop camera"}
                onClick={toggleVideo}
                disabled={!isJoined || !hasLocalVideo}
                className={
                  cameraLooksOff
                    ? "video-control-button control-disabled-state"
                    : "video-control-button"
                }
              >
                {cameraLooksOff ? <VideocamOff /> : <Videocam />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title="Leave consultation">
          <span>
            <IconButton
              aria-label="Leave consultation"
              onClick={leaveChannel}
              disabled={!isJoined && !isJoining}
              className="video-control-button leave-call-button"
            >
              <CallEnd />
            </IconButton>
          </span>
        </Tooltip>

        {startWithVideo && (
          <Tooltip title="Switch camera">
            <span>
              <IconButton
                aria-label="Switch camera"
                onClick={() => setOpenCameraModal(true)}
                disabled={!isJoined || !hasLocalVideo}
                className="video-control-button"
              >
                <SwitchCamera />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip
          title={isLocalStreamVisible ? "Hide self view" : "Show self view"}
        >
          <IconButton
            aria-label={
              isLocalStreamVisible ? "Hide self view" : "Show self view"
            }
            onClick={() => setIsLocalStreamVisible((visible) => !visible)}
            className="video-control-button"
          >
            {isLocalStreamVisible ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </Tooltip>

        {startWithVideo && (
          <Tooltip title="Video quality">
            <span>
              <IconButton
                aria-label="Video quality"
                onClick={() => setIsQualityDialogOpen(true)}
                disabled={!isJoined || !hasLocalVideo}
                className="video-control-button"
              >
                <HighQuality />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {startWithVideo && (
        <CameraSwitchModal
          open={openCameraModal}
          onClose={() => setOpenCameraModal(false)}
          onSwitchCamera={handleSwitchCameraComplete}
          localTrack={localTracks.current.videoTrack}
        />
      )}
    </Box>
  );
};

export default VideoCall;
